import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Clock, Gem, Heart, MessageCircle, Rewind, Settings, SlidersHorizontal, User } from 'lucide-react'
import {
  type Candidate,
  ApiError,
  fetchFeed,
  fetchLikesYouCount,
  reportPaywallShown,
  rewind,
  setPause,
  swipe,
  updateLocation,
} from '../lib/api'
import { requestLocation } from '../lib/geolocation'
import { hapticNotification } from '../lib/haptics'
import FeedFilterSheet from '../components/FeedFilterSheet'
import NotificationBadge from '../components/NotificationBadge'
import SettingsSheet from '../components/SettingsSheet'
import Skeleton, { SwipeCardSkeleton } from '../components/Skeleton'
import SwipeDeck, { type SwipeDeckHandle } from '../components/SwipeDeck'
import { useUserStore } from '../store/userStore'

// Pool size fetched per /feed call, and how many cards from the end of the
// in-memory deck we start silently prefetching the next batch at, so the
// feed feels like a continuous/infinite scroll instead of per-pool refreshes.
const BATCH_SIZE = 10
const PREFETCH_THRESHOLD = 3

type PageState =
  | { status: 'loading' }
  | { status: 'location_prompt' }
  | { status: 'location_denied'; message: string }
  | { status: 'ready'; candidates: Candidate[]; index: number; hasMore: boolean }
  | { status: 'empty' }
  | { status: 'limit_reached'; limit: number; resetsAt: string | null }
  | { status: 'error'; message: string }

// Appends newly-fetched candidates to an existing list, dropping any that are
// already present (already-in-deck or already-swiped-but-not-yet-persisted).
function dedupeAppend(existing: Candidate[], incoming: Candidate[]): Candidate[] {
  const seen = new Set(existing.map((c) => c.user_id))
  const fresh = incoming.filter((c) => !seen.has(c.user_id))
  return [...existing, ...fresh]
}

export default function SwipePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { me, setPaused } = useUserStore()
  const [state, setState] = useState<PageState>({ status: 'loading' })
  const [committing, setCommitting] = useState(false)
  const [likesYou, setLikesYou] = useState(0)
  const [showFilters, setShowFilters] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const deckRef = useRef<SwipeDeckHandle>(null)
  const likesPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const prefetchingRef = useRef(false)

  // revealUserID comes from the "See Who Likes You" tap (LikesPage ->
  // /swipe?reveal=<id>, see docs/monetization-roadmap.md "Kejar Premium
  // Feature" #4) — passed through to GET /feed once, on the very next fetch
  // only. It's intentionally NOT read from `searchParams` here (only by the
  // mount effect below, which clears the param immediately after) — the
  // background prefetch effect calls loadFeed indirectly via fetchFeed
  // without it, so a stale reveal id in the URL can never re-trigger the
  // injection on a later, unrelated fetch.
  async function loadFeed(revealUserID?: number) {
    setState({ status: 'loading' })
    try {
      const { candidates, revealed } = await fetchFeed({ limit: BATCH_SIZE, reveal: revealUserID })
      if (revealUserID !== undefined && revealed === false) {
        // The person tapped from the Likes screen no longer qualifies
        // (unapproved photo since, blocked, etc.) — say so honestly rather
        // than silently showing an unrelated deck.
        showToast(t('swipe.revealUnavailable'))
      }
      if (candidates.length === 0) {
        setState({ status: 'empty' })
      } else {
        setState({
          status: 'ready',
          candidates,
          index: 0,
          hasMore: candidates.length === BATCH_SIZE,
        })
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) {
        const body = e.message
        if (body.includes('location_required')) {
          setState({ status: 'location_prompt' })
          return
        }
      }
      setState({
        status: 'error',
        message: e instanceof Error ? e.message : t('common.genericError'),
      })
    }
  }

  useEffect(() => {
    const revealParam = searchParams.get('reveal')
    const revealUserID = revealParam ? Number(revealParam) : undefined
    loadFeed(revealUserID)
    if (revealParam) {
      // Consume the deep-link param immediately so it never re-fires on a
      // later navigation/re-render — mirrors App.tsx's start_param guard.
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once on mount only
  }, [])

  // Silent background prefetch: once the in-memory deck is running low,
  // pull the next batch and append it so swiping never hits a manual
  // "Refresh" interruption while more candidates are actually available.
  useEffect(() => {
    if (state.status !== 'ready' || !state.hasMore || prefetchingRef.current) return
    const remaining = state.candidates.length - state.index
    if (remaining > PREFETCH_THRESHOLD) return

    prefetchingRef.current = true
    fetchFeed({ limit: BATCH_SIZE })
      .then(({ candidates: next }) => {
        setState((prev) => {
          if (prev.status !== 'ready') return prev
          const merged = dedupeAppend(prev.candidates, next)
          return { ...prev, candidates: merged, hasMore: next.length === BATCH_SIZE }
        })
      })
      .catch(() => {
        // Silent failure: the swipe-time fallback in handleSwipe covers
        // genuine exhaustion; a transient prefetch error just retries next tick.
      })
      .finally(() => {
        prefetchingRef.current = false
      })
  }, [state])

  // Poll the likes-you count every 10s, plus refresh immediately on tab
  // visibility change so the badge is always current. In-app chat is
  // retired (docs/bot-migration-decision.md's "Phase 1.5 — Chat
  // retirement"), so there's no more message-unread count to poll here —
  // MatchesPage no longer has live-changing data either.
  useEffect(() => {
    function loadLikesYou() {
      if (document.visibilityState === 'visible') {
        fetchLikesYouCount()
          .then(({ likes_you }) => setLikesYou(likes_you))
          .catch(() => {})
      }
    }
    loadLikesYou()
    likesPollRef.current = setInterval(loadLikesYou, 10000)
    document.addEventListener('visibilitychange', loadLikesYou)
    return () => {
      if (likesPollRef.current) clearInterval(likesPollRef.current)
      document.removeEventListener('visibilitychange', loadLikesYou)
    }
  }, [])

  async function handleResume() {
    setPaused(false)
    try {
      await setPause(false)
    } catch {
      setPaused(true)
      return
    }
    loadFeed()
  }

  async function handleEnableLocation() {
    try {
      const { lat, lon } = await requestLocation()
      await updateLocation(lat, lon)
      await loadFeed()
    } catch (e) {
      setState({
        status: 'location_denied',
        message: e instanceof Error ? e.message : 'Location access denied',
      })
    }
  }

  // Shows a brief transient message (2.5s) — used for any non-fatal swipe
  // hiccup (crush balance empty, rewind limit/errors) that shouldn't nuke
  // the whole session into the generic error screen.
  function showToast(message: string) {
    setToastMessage(message)
    setTimeout(() => setToastMessage(null), 2500)
  }

  // Advances the deck to the next card — used both after a successful swipe
  // and after a Crush fails for insufficient balance (the card has already
  // flown off-screen by the time that error surfaces, see SwipeCard's
  // programmatic-exit animation, so there's no sane "undo the swipe" —
  // the deck just continues as if it were a normal swipe).
  async function advanceDeck(current: Extract<PageState, { status: 'ready' }>) {
    const nextIndex = current.index + 1
    if (nextIndex < current.candidates.length) {
      setState({ ...current, index: nextIndex })
    } else if (!current.hasMore) {
      setState({ status: 'empty' })
    } else {
      // Background prefetch hasn't landed yet (slow network) — fall back
      // to a blocking fetch so the swipe still advances seamlessly.
      try {
        const { candidates: next } = await fetchFeed({ limit: BATCH_SIZE })
        const merged = dedupeAppend(current.candidates, next)
        if (merged.length <= current.candidates.length) {
          setState({ status: 'empty' })
        } else {
          setState({
            candidates: merged,
            index: nextIndex,
            status: 'ready',
            hasMore: next.length === BATCH_SIZE,
          })
        }
      } catch {
        setState({ status: 'empty' })
      }
    }
  }

  async function handleSwipe(action: 'like' | 'skip' | 'crush') {
    if (state.status !== 'ready' || committing) return
    setCommitting(true)
    const candidate = state.candidates[state.index]
    try {
      const result = await swipe(candidate.user_id, action)
      if (result.matched && result.match_id) {
        hapticNotification('success')
        // In-app chat is retired (docs/bot-migration-decision.md's "Phase
        // 1.5 — Chat retirement") — land on the matches list, opened
        // straight to this match's profile sheet, same target as the bot's
        // own match notification deep link (see App.tsx's match_<id>
        // start_param handling).
        navigate(`/matches?open=${result.match_id}`)
        return
      }
      await advanceDeck(state)
    } catch (e) {
      if (e instanceof ApiError && e.status === 429 && e.body?.error === 'daily_limit_reached') {
        // Distinct from the feed's anti-scrape 429 (`rate_limited`), which
        // has a different body shape and should fall through to the
        // generic retryable error screen below instead of this dead-ish end.
        setState({
          status: 'limit_reached',
          limit: typeof e.body?.limit === 'number' ? e.body.limit : 10,
          resetsAt: typeof e.body?.resets_at === 'string' ? e.body.resets_at : null,
        })
      } else if (
        e instanceof ApiError &&
        e.status === 422 &&
        e.message.includes('insufficient_crush_balance')
      ) {
        // Nothing was persisted server-side — nudge straight to the Crush
        // paywall instead of a toast, mirroring OMI's own behavior (see
        // docs/monetization-roadmap.md).
        reportPaywallShown('crush_balance')
        navigate('/buy-crush')
        return
      } else {
        setState({
          status: 'error',
          message: e instanceof Error ? e.message : t('common.swipeFailed'),
        })
      }
    } finally {
      setCommitting(false)
    }
  }

  // Undoes the caller's most recent swipe. The button is visible to
  // everyone, but the daily quota is still VIP-exclusive (0/day free tier,
  // 1/day VIP/admin — see docs/monetization-roadmap.md), so a free caller's
  // very first tap already 429s; see the 429 branch below for how that's
  // told apart from a VIP/admin who genuinely used their one rewind today.
  // On success, the undone candidate is spliced back in at the current
  // index so their card is the next one shown; if the backend has nothing
  // to return (they no longer qualify under the viewer's own filters), the
  // undo still happened server-side, there's just no card to reinsert.
  async function handleRewind() {
    if (state.status !== 'ready' || committing) return
    setCommitting(true)
    try {
      const result = await rewind()
      if (result.candidate) {
        setState({
          ...state,
          candidates: [
            ...state.candidates.slice(0, state.index),
            result.candidate,
            ...state.candidates.slice(state.index),
          ],
        })
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        // Daily rewind quota exhausted. A VIP/admin caller genuinely used
        // their one rewind today — a plain toast (matching
        // nothingToRewind/cannotRewindMatched below) is the honest
        // response. A free caller never had a quota to begin with (0/day),
        // so their very first tap lands here too — for them this is a
        // paywall moment, not an error, mirroring the insufficient-Crush
        // redirect below and the like-limit screen's "Upgrade VIP" CTA.
        if (me?.is_admin || me?.is_vip) {
          showToast(t('swipe.rewindLimitReached'))
        } else {
          reportPaywallShown('rewind')
          navigate('/buy-vip?reason=rewind')
        }
      } else if (e instanceof ApiError && e.status === 422 && e.message.includes('nothing_to_rewind')) {
        showToast(t('swipe.nothingToRewind'))
      } else if (e instanceof ApiError && e.status === 422 && e.message.includes('cannot_rewind_matched')) {
        showToast(t('swipe.cannotRewindMatched'))
      } else {
        setState({
          status: 'error',
          message: e instanceof Error ? e.message : t('common.swipeFailed'),
        })
      }
    } finally {
      setCommitting(false)
    }
  }

  return (
    <div className="relative flex min-h-dvh flex-col bg-tg-bg">
      <div className="flex items-center justify-between px-4 pt-3">
        <button
          onClick={() => navigate('/profile')}
          className="flex items-center gap-1.5 rounded-xl bg-tg-secondary-bg px-3 py-1.5 text-sm font-medium text-tg-text"
        >
          <User className="h-4 w-4" />
          {t('swipe.profile')}
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/likes')}
            aria-label={t('likes.title')}
            className="flex items-center gap-1.5 rounded-xl bg-tg-secondary-bg px-3 py-1.5 text-sm font-medium text-tg-text"
          >
            <Heart className="h-4 w-4" />
            <NotificationBadge count={likesYou} />
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="rounded-xl bg-tg-secondary-bg p-2 text-tg-text"
            aria-label={t('settings.title')}
          >
            <Settings className="h-5 w-5" />
          </button>
          <button
            onClick={() => setShowFilters(true)}
            className="flex items-center gap-1.5 rounded-xl bg-tg-secondary-bg px-3 py-1.5 text-sm font-medium text-tg-text"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {t('swipe.filters')}
          </button>
          <button
            onClick={() => navigate('/matches')}
            className="flex items-center gap-1.5 rounded-xl bg-tg-secondary-bg px-3 py-1.5 text-sm font-medium text-tg-text"
          >
            <MessageCircle className="h-4 w-4" />
            {t('swipe.matches')}
          </button>
        </div>
      </div>

      {me?.has_photo && !me?.has_approved_photo && (
        <button
          onClick={() => navigate('/photos')}
          className="mx-4 mt-3 flex items-center gap-2 rounded-xl bg-amber-100 px-3 py-2 text-left text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
        >
          <Clock className="h-4 w-4 shrink-0" />
          <span className="flex-1">{t('swipe.photoPendingBanner')}</span>
          <span className="shrink-0 font-medium underline">{t('swipe.photoPendingAction')}</span>
        </button>
      )}

      {showSettings && <SettingsSheet onClose={() => setShowSettings(false)} />}

      {showFilters && (
        <FeedFilterSheet
          isAdmin={me?.is_admin ?? false}
          onClose={() => setShowFilters(false)}
          onSaved={() => {
            setShowFilters(false)
            loadFeed()
          }}
        />
      )}

      {me?.is_paused && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-xl font-semibold text-tg-text">{t('swipe.pausedTitle')}</p>
          <p className="text-sm text-tg-hint">{t('swipe.pausedMessage')}</p>
          <button
            onClick={handleResume}
            className="rounded-xl bg-primary px-6 py-3 font-medium text-white"
          >
            {t('swipe.resume')}
          </button>
        </div>
      )}

      {!me?.is_paused && state.status === 'loading' && (
        <div className="flex flex-1 flex-col px-4 py-6">
          <div className="relative flex-1">
            <SwipeCardSkeleton />
          </div>
          <div className="mt-6 flex gap-4">
            <Skeleton className="h-14 flex-1 rounded-xl" />
            <Skeleton className="h-14 flex-1 rounded-xl" />
          </div>
        </div>
      )}

      {!me?.is_paused && state.status === 'location_prompt' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-xl font-semibold text-tg-text">{t('swipe.findPeopleNearYou')}</p>
          <p className="text-sm text-tg-hint">{t('swipe.needLocation')}</p>
          <button
            onClick={handleEnableLocation}
            className="rounded-xl bg-primary px-6 py-3 font-medium text-white"
          >
            {t('swipe.enableLocation')}
          </button>
        </div>
      )}

      {!me?.is_paused && state.status === 'location_denied' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-lg font-semibold text-tg-text">{t('swipe.locationRequired')}</p>
          <p className="text-sm text-tg-hint">{state.message}</p>
          <p className="text-xs text-tg-hint">{t('swipe.allowLocationAccess')}</p>
          <button
            onClick={handleEnableLocation}
            className="rounded-xl bg-tg-secondary-bg px-6 py-3 font-medium text-tg-text"
          >
            {t('swipe.tryAgain')}
          </button>
        </div>
      )}

      {!me?.is_paused && state.status === 'empty' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-xl font-semibold text-tg-text">{t('swipe.noMoreProfiles')}</p>
          <p className="text-sm text-tg-hint">{t('swipe.noMoreCandidates')}</p>
          <button
            onClick={() => loadFeed()}
            className="rounded-xl bg-tg-secondary-bg px-6 py-3 font-medium text-tg-text"
          >
            {t('swipe.refresh')}
          </button>
        </div>
      )}

      {!me?.is_paused && state.status === 'limit_reached' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-xl font-semibold text-tg-text">{t('swipe.dailyLimitReached')}</p>
          <p className="text-sm text-tg-hint">
            {t('swipe.dailyLimitMessage', { count: state.limit })}
          </p>
          {state.resetsAt && (
            <p className="text-sm text-tg-hint">
              {t('swipe.dailyLimitResetsAt', {
                time: new Date(state.resetsAt).toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                }),
              })}
            </p>
          )}
          {me?.payments_enabled && !me?.is_vip && (
            <button
              onClick={() => {
                reportPaywallShown('likes')
                navigate('/buy-vip?reason=likes')
              }}
              className="rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 px-6 py-3 font-bold text-white"
            >
              {t('swipe.upgradeForMoreLikes')}
            </button>
          )}
        </div>
      )}

      {!me?.is_paused && state.status === 'error' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-sm text-red-500">{state.message}</p>
          <button
            onClick={() => loadFeed()}
            className="rounded-xl bg-tg-secondary-bg px-6 py-3 font-medium text-tg-text"
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* Transient toast — crush-balance-empty, rewind limit/errors (see
          handleSwipe/handleRewind); the swipe deck itself keeps going. */}
      {toastMessage && (
        <div className="absolute inset-x-4 top-12 z-20 flex items-center justify-center rounded-xl bg-black/70 py-2 text-sm text-white">
          {toastMessage}
        </div>
      )}

      {!me?.is_paused && state.status === 'ready' && (
        <div className="flex flex-1 flex-col px-4 py-6">
          <SwipeDeck
            ref={deckRef}
            candidates={state.candidates}
            index={state.index}
            onSwipe={handleSwipe}
            disabled={committing}
          />

          <div className="mt-6 flex gap-4">
            {/* Rewind — visible to everyone as a discovery path into the VIP
                paywall; the daily quota itself is still VIP-exclusive (0/day
                free tier) — see handleRewind's 429 branch below and
                docs/monetization-roadmap.md. */}
            <button
              onClick={handleRewind}
              disabled={committing}
              className="flex items-center justify-center rounded-xl bg-tg-secondary-bg px-4 py-4 text-tg-text disabled:opacity-40"
              aria-label={t('swipe.rewind')}
            >
              <Rewind className="h-5 w-5" />
            </button>
            <button
              onClick={() => deckRef.current?.swipe('skip')}
              disabled={committing}
              className="flex-1 rounded-xl bg-tg-secondary-bg py-4 text-lg font-medium text-tg-text disabled:opacity-40"
            >
              {t('swipe.skip')}
            </button>
            <button
              onClick={() => deckRef.current?.swipe('like')}
              disabled={committing}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-4 text-lg font-medium text-white disabled:opacity-40"
            >
              <Heart className="h-5 w-5 fill-current" />
              {t('swipe.like')}
            </button>
            {/* Crush — visible to everyone; spending is balance-only
                server-side (see CrushRepo.Spend), so a free user with a
                purchased balance can already send one. Insufficient balance
                redirects to the paywall below, same as Rewind's 429. */}
            <button
              onClick={() => deckRef.current?.swipe('crush')}
              disabled={committing}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cyan-500 py-4 text-lg font-medium text-white disabled:opacity-40"
            >
              <Gem className="h-5 w-5 fill-current" />
              {t('swipe.crush')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
